import {
	GstLineAmounts,
	GstTotals,
	PriceEntryMode,
	TaxCode,
	previewLine,
	previewTotals,
} from "../../../utils/finance/gst";
import { centsToDollarsNumber, dollarsToCents } from "../../../utils/finance/money";
import {
	CreateInvoiceItemRequest,
	InvoiceItem,
	InvoiceLineSourceType,
} from "../../../types/finance/FinanceTypes";
import {
	CatalogueAppointmentType,
	CatalogueProduct,
	CatalogueVariant,
} from "../../../types/finance/CatalogueTypes";

/**
 * The pure half of `InvoiceLineEditor` — the draft line shape and the four functions that turn
 * it into a preview and into a request body. Kept out of the `.tsx` so it can be tested without
 * rendering antd, and so nothing in here can quietly grow a dependency on React state.
 */

/**
 * One line as the operator is typing it.
 *
 * ⚠️ `unitPriceDollars` is what was TYPED — a dollars `number` straight off antd's `InputNumber`,
 * nullable while the field is empty. It is never sent and never summed: every arithmetic path
 * goes through `draftUnitPriceCents` first. That is the single conversion point, and it is the
 * one place a float is allowed to exist.
 */
export interface InvoiceLineDraft {
	/** Stable React key. Not sent; the server orders by `sortOrder`, which is the array index. */
	key: string;
	description: string;
	quantity: number;
	unitPriceDollars: number | null;
	taxCode: TaxCode;

	// ── Provenance (S12) ─────────────────────────────────────────────────────────────────────
	// Every key is OPTIONAL so a draft built anywhere without them is still a valid draft, and an
	// absent `sourceType` means exactly what `manual` means. `newLineDraft` writes them all
	// explicitly anyway; read them through `lineSourceType` / `draftsToRequestItems`, never raw.
	sourceType?: InvoiceLineSourceType;
	sourceProductId?: number | null;
	sourceVariantId?: number | null;
	sourceAppointmentTypeId?: number | null;
	sourceSku?: string | null;
}

let keySeq = 0;

/** A blank line, defaulting to the clinic's configured tax code. */
export function newLineDraft(taxCode: TaxCode = TaxCode.GST_FREE_HEALTH): InvoiceLineDraft {
	keySeq += 1;
	return {
		key: `line-${keySeq}`,
		description: "",
		quantity: 1,
		unitPriceDollars: null,
		taxCode,
		sourceType: InvoiceLineSourceType.MANUAL,
		sourceProductId: null,
		sourceVariantId: null,
		sourceAppointmentTypeId: null,
		sourceSku: null,
	};
}

/** The line's source, normalised. An absent one is `manual` — that is what it always meant. */
export function lineSourceType(line: InvoiceLineDraft): InvoiceLineSourceType {
	return line?.sourceType ?? InvoiceLineSourceType.MANUAL;
}

/**
 * Rebuild a draft from a stored line, for editing an existing draft invoice.
 *
 * The unit price comes from `unitPriceCents` (the integer), NOT from the `unitPrice` dollar
 * string — the string is a rendering of the same value and re-parsing it would put a float where
 * an exact integer was already available.
 */
export function draftFromItem(item: InvoiceItem): InvoiceLineDraft {
	keySeq += 1;
	return {
		key: `line-${item.id ?? keySeq}`,
		description: item.description ?? "",
		quantity: Number(item.quantity ?? 1),
		unitPriceDollars: Number(item.unitPriceCents ?? 0) / 100,
		taxCode: (item.taxCode as TaxCode) ?? TaxCode.GST_FREE_HEALTH,
		// ⚠️ Provenance is restored, not dropped. `PUT /invoices/:id` DELETES and re-inserts every
		// line row, so a field that does not come back on the edit body is gone — an edit that
		// changed a typo would otherwise silently un-source every line on the invoice, taking the
		// snapshot and (at issue) the stock movement with it.
		sourceType: item.sourceType ?? InvoiceLineSourceType.MANUAL,
		sourceProductId: item.sourceProductId ?? null,
		sourceVariantId: item.sourceVariantId ?? null,
		sourceAppointmentTypeId: item.sourceAppointmentTypeId ?? null,
		sourceSku: item.sourceSku ?? null,
	};
}

// ══ The picker's pure half (S12) ══════════════════════════════════════════════════════════════

/**
 * What the practitioner chose in the picker.
 *
 * ⚠️ **A product pick may legitimately carry NO variant.** Parent decision D6 is that the picker
 * asks for product AND variant, both explicit, and there is no `isDefaultVariant` to preselect
 * from — finance's projection omits it deliberately, because "a default invites a silent
 * preselection of the wrong SKU". So the product arrives first, on its own, and the variant lands
 * as a second pick.
 */
export type CataloguePick =
	| { source: "product"; product: CatalogueProduct; variant?: CatalogueVariant | null }
	| { source: "appointment-type"; appointmentType: CatalogueAppointmentType };

/**
 * Shopify's placeholder title for the auto-created single variant, which commerce reproduces
 * verbatim (`ProductsController` writes `'Default Title'` on product create). It is not a variant
 * NAME and must never reach the description a patient reads on a tax invoice.
 */
const PLACEHOLDER_VARIANT_TITLES = ["default title"];

function isMeaningfulVariantTitle(product: CatalogueProduct, title?: string): boolean {
	const trimmed = (title ?? "").trim();

	return (
		trimmed.length > 0 &&
		!PLACEHOLDER_VARIANT_TITLES.includes(trimmed.toLowerCase()) &&
		trimmed.toLowerCase() !== (product?.name ?? "").trim().toLowerCase()
	);
}

/**
 * The description a product pick writes. **This is patient-facing text**, so it is the product
 * name plus the variant only when the variant says something the product name does not.
 */
function productDescription(product: CatalogueProduct, variant?: CatalogueVariant | null): string {
	const name = (product?.name ?? "").trim();

	return variant && isMeaningfulVariantTitle(product, variant.title)
		? `${name} — ${variant.title.trim()}`
		: name;
}

/**
 * Set a line's source WITHOUT picking anything — the Manual / Product / Appointment selector.
 *
 * ⚠️ **Every source id is cleared on every switch, including a switch between the two catalogue
 * sources.** `buildItems()` writes `sourceProductId` / `sourceVariantId` /
 * `sourceAppointmentTypeId` straight through regardless of `sourceType`, so a line left holding
 * the previous source's ids would persist a provenance that contradicts itself — and a stale
 * variant id is what a fulfilment reads to decide which stock to move.
 *
 * What is deliberately KEPT is everything the practitioner can see: the description, the quantity,
 * the price and the tax code. Switching back to Manual is how someone edits a catalogue line's
 * wording by hand; blanking the row under them would make that impossible.
 */
export function setLineSource(
	line: InvoiceLineDraft,
	sourceType: InvoiceLineSourceType,
): InvoiceLineDraft {
	return {
		...line,
		sourceType,
		sourceProductId: null,
		sourceVariantId: null,
		sourceAppointmentTypeId: null,
		sourceSku: null,
	};
}

/**
 * Apply a catalogue pick to a line — the ONE place the prefill happens.
 *
 * ⚠️ **A `null` catalogue price leaves the price field exactly as it was.** `unitPriceCents` is
 * `null` for a genuinely unpriced item, which is the COMMON case for an appointment type
 * (`default_price` is nullable upstream and usually is). Writing 0 would render `$0.00`, which
 * reads to a patient as "nothing is owed". A price of `0` is a different thing — a genuinely free
 * item — and it IS written.
 *
 * ⚠️ **No money conversion happens here beyond `centsToDollarsNumber`.** `unitPriceCents` is
 * already integer cents from finance (the one money boundary); `dollarsToCents` must never be
 * pointed at it, since it returns 0 for anything it cannot parse.
 *
 * The price stays editable afterwards (parent decision D4) — this fills the field, it does not own
 * it. The catalogue's own figure is snapshotted server-side, so a discount shows up as a variance.
 *
 * `defaultTaxCode` is the clinic default and is what an appointment type takes (a medical service
 * is GST-free); a product takes `gst`, because a physical good is a taxable supply in Australia
 * (parent decision A2). Both remain editable, and the server applies the same two defaults for a
 * line that names no tax code at all.
 */
export function applyCataloguePick(
	line: InvoiceLineDraft,
	pick: CataloguePick,
	defaultTaxCode: TaxCode = TaxCode.GST_FREE_HEALTH,
): InvoiceLineDraft {
	if (pick.source === "appointment-type") {
		const type = pick.appointmentType;

		return {
			...setLineSource(line, InvoiceLineSourceType.APPOINTMENT_TYPE),
			description: (type?.name ?? "").trim(),
			unitPriceDollars: prefilledPrice(line, type?.unitPriceCents),
			taxCode: defaultTaxCode,
			sourceAppointmentTypeId: type?.appointmentTypeId ?? null,
		};
	}

	const { product, variant } = pick;

	return {
		...setLineSource(line, InvoiceLineSourceType.COMMERCE_PRODUCT),
		description: productDescription(product, variant),
		// A product carries no price of its own — stock and price are both keyed by VARIANT — so
		// picking the product alone leaves whatever was in the field. The variant pick fills it.
		unitPriceDollars: variant ? prefilledPrice(line, variant.unitPriceCents) : line.unitPriceDollars,
		taxCode: TaxCode.GST,
		sourceProductId: product?.productId ?? null,
		sourceVariantId: variant?.variantId ?? null,
		sourceSku: (variant?.sku ?? "").trim() || null,
	};
}

/** The catalogue price as typed dollars — or the untouched field when there is no price. */
function prefilledPrice(line: InvoiceLineDraft, unitPriceCents?: number | null): number | null {
	return unitPriceCents === null || unitPriceCents === undefined
		? line.unitPriceDollars
		: centsToDollarsNumber(unitPriceCents);
}

/** The typed dollars as exact integer cents. An empty field is 0, not NaN. */
export function draftUnitPriceCents(line: InvoiceLineDraft): number {
	return dollarsToCents(line.unitPriceDollars ?? 0);
}

/** One line's ex / tax / inc preview. Never throws on a half-typed line. */
export function previewDraftLine(line: InvoiceLineDraft, mode: PriceEntryMode): GstLineAmounts {
	return previewLine(
		{
			quantity: Number.isSafeInteger(Number(line.quantity)) ? Number(line.quantity) : 0,
			unitPriceCents: draftUnitPriceCents(line),
			taxCode: line.taxCode,
		},
		mode,
	);
}

/**
 * The whole draft's preview. Per line, then sum — the ATO ordering the backend uses, so the
 * number under the form is the number that will be stored.
 */
export function previewDraftTotals(lines: InvoiceLineDraft[], mode: PriceEntryMode): GstTotals {
	return previewTotals(
		lines.map((line) => ({
			quantity: Number.isSafeInteger(Number(line.quantity)) ? Number(line.quantity) : 0,
			unitPriceCents: draftUnitPriceCents(line),
			taxCode: line.taxCode,
		})),
		mode,
	);
}

/**
 * Draft lines -> the `items` array of a create/update request.
 *
 * ⚠️ **Cents only, and no computed amounts.** The request carries the three INPUTS
 * (`quantity`, `unitPriceCents`, `taxCode`) and the server derives ex/tax/inc itself. Sending a
 * client-computed total would make the browser's arithmetic authoritative for a legal document.
 *
 * `sortOrder` is the array index, which is what makes a re-ordered draft come back in the order
 * the operator left it.
 *
 * ⚠️ **Provenance is sent on EVERY line, nulls included** — a manual line sends
 * `sourceType: 'manual'` and four nulls rather than omitting the keys. The server would default an
 * absent `sourceType` to `manual` either way, but `PUT /invoices/:id` deletes and re-inserts every
 * row, so being explicit is what makes "the practitioner switched this line back to Manual" and
 * "this client is too old to know about provenance" different requests.
 *
 * ⚠️ `catalogueUnitPriceCents` is deliberately NOT sent — see `CreateInvoiceItemRequest`.
 */
export function draftsToRequestItems(lines: InvoiceLineDraft[]): CreateInvoiceItemRequest[] {
	return lines.map((line, index) => ({
		description: line.description?.trim() ?? "",
		quantity: Number(line.quantity ?? 1),
		unitPriceCents: draftUnitPriceCents(line),
		taxCode: line.taxCode,
		sortOrder: index,
		sourceType: lineSourceType(line),
		sourceProductId: line.sourceProductId ?? null,
		sourceVariantId: line.sourceVariantId ?? null,
		sourceAppointmentTypeId: line.sourceAppointmentTypeId ?? null,
		sourceSku: line.sourceSku ?? null,
	}));
}

/**
 * A line the operator has genuinely filled in. Used to drop a trailing blank row before submit,
 * so "add line, change your mind, save" does not create an empty `""` line — which the server
 * rejects with a validation 400 the operator cannot connect to anything they did.
 */
export function isBlankDraft(line: InvoiceLineDraft): boolean {
	return (
		(line.description ?? "").trim().length === 0 &&
		(line.unitPriceDollars === null || line.unitPriceDollars === undefined)
	);
}
